import React from 'react';
import { RoutesType } from '../../router.config';
import { createHashHistory, UnregisterCallback } from 'history';
import './main-page.scss'
import { isTablet, isMobileOnly } from 'react-device-detect';
import _ from 'lodash';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import { makeAutoObservable, runInAction } from 'mobx';
import { routerManager } from '../../router.manager';
import { accountState } from '../my-account/account-state';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { Restaurant } from '../../models';
import LSM from '../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import { DefaultWebsocketClient } from '../../modules/websocket/module';
import { apiManage_rbp } from '../../request';
import { createPageReqDocker, removeReq } from '../../request/http';
import setLoading from '../../component/linear-determinate';
import { autoSet } from '../../modules/cache/auto-set-storage';
import Card from '@mui/material/Card';
import { autorun, type IReactionDisposer } from 'mobx';
import { observer } from 'mobx-react-lite';
import { tunnelStatusStore } from './tunnel-status-state';
import { resolveStaffRestaurantSelection } from '../../utils/staff-restaurant-selection';
import { cleanAccountCache } from '../../constants/tool.check-token';

export default class MainPageState {

    constructor() {
        makeAutoObservable(this);

        // 顶部栏数据
        this.topBarState = MobxObjectsManager.get(TopBarState);
    }

    setProps(props: Props) {
        this.open = !(isTablet || isMobileOnly);
        this.routes = props.routes;
    }


    // 路由监听
    unlisten?: UnregisterCallback;


    // 侧边栏宽度
    public sidebarWidth = {
        // 默认展开
        expand: '240px',
        // 缩小
        reduced: '84px',
        // 隐藏
        hidden: '0px'
    };
    // 顶部栏
    topBarState: TopBarState;
    // 路由
    public routes: RoutesType[] = [];
    // 展开\收缩侧边栏
    public open: boolean = false;
    // 展开的ids
    public openTabIds: (string | undefined)[] = [];
    // 展开状态
    public collapseState: boolean = false;
    // 展开的ids
    public openTabletTabIds: (string | undefined)[] = [];
    // 是否是移动设备
    public isMobileDevice: boolean = isTablet || isMobileOnly;
    // 覆盖层
    public mobileMask: boolean = false;
    // 所属餐厅列表
    public belongedRestaurant: Restaurant[] = [];
    // 当前选中的餐厅Index
    public currentRestaurant: Restaurant = {};
    // 账号信息
    public accountType: 'ADMIN' | 'STAFF' | 'RESELLER' | '' = '';
    // 餐厅货币
    public rbpCurrency: string = LSM.get(LOCAL_STORAGE_KEYS.RBP_RESTAURANT_CURRENCY) || '€';

    // *********************
    // Default Function
    // *********************

    async init() {
        this.scrollIntoTop();
        // 子页面跳转，刷新侧边栏和导航
        this.unlisten = createHashHistory().listen(() => {
            this.scrollIntoTop();
            this.autoExpend();
        })

        this.autoExpend();
        // 获取默认配置（STAFF 会先重拉绑定餐厅，须完成后再拉业务数据）
        await this.getDefaultConfig();
        // 获取数据
        await this.getAppData();
        // 连接WS
        // await apiManage.websocket_init()

        /** 全局隧道状态：仅随聚焦餐厅变化校验，供 HookDecorator / 类组件装饰器复用 */
        tunnelStatusStore.startWatch();
    }
    /**
     * 获取默认配置：STAFF 刷新时重拉绑定餐厅，避免超管改绑后必须重新登录。
     */
    async getDefaultConfig() {
        const account: { belonged_restaurant?: Restaurant[], account_type: 'ADMIN' | 'STAFF' } = LSM.get(LOCAL_STORAGE_KEYS.ACCOUNT) || {};
        // 本地缓存的账号类型（STAFF 才处理餐厅下拉）
        this.setAccountType(account.account_type);
        if (account.account_type !== 'STAFF') {
            this.setRbpCurrency('€');
            return;
        }
        // 服务端最新绑定；失败为 null，解析时回退缓存
        const refreshedBelongedRestaurant = await accountState.refreshStaffProfileForMainFrame();
        const lastSelectedRestaurant: Restaurant | undefined = LSM.get(LOCAL_STORAGE_KEYS.DEFAULT_RESTAURANT);
        const selection = resolveStaffRestaurantSelection({
            refreshedBelongedRestaurant,
            cachedBelongedRestaurant: account.belonged_restaurant || [],
            lastSelectedRestaurant,
        });
        if (selection.shouldLogoutBecauseUnbound) {
            GlobalNotification.warning(i18n.t('main_page_alert_no_bound_restaurant'));
            cleanAccountCache(true);
            return;
        }
        this.setBelongedRestaurant(selection.belongedRestaurant);
        this.setCurrentRestaurant(selection.currentRestaurant, true);
        if (selection.currentRestaurant.string_id) {
            await this.getRBPRestaurantCountry();
        }
    }

    setAccountType(value: 'ADMIN' | 'STAFF') {
        this.accountType = value;
    }

    /**
     * 设置所属餐厅列表
     * @param value
     */
    setBelongedRestaurant(value: Restaurant[]) {
        this.belongedRestaurant = value
    }

    /**
     * 设置所属餐厅
     * @param value
     */
    @autoSet(LOCAL_STORAGE_KEYS.DEFAULT_RESTAURANT)
    setCurrentRestaurant(value: Restaurant, init?: boolean) {
        if (init) {
            this.currentRestaurant = value;
            return;
        }
        this.currentRestaurant = value;
        DefaultWebsocketClient.emit('CURRENT_RESTAURANT', { string_id: value.string_id });
        this.getRBPRestaurantCountry();
    }
    /**
     * 移除监听
     */
    destroy() {
        this?.unlisten?.();
        tunnelStatusStore.stopWatch();
    }

    /**
     * 自动展开
     */
    autoExpend() {
        const { pathname } = createHashHistory().location;

        let targetId = '';
        const current = this.routes.find((n) => pathname.includes(n.path));
        let pathText = '';
        if (current) {
            // 二级菜单
            if (current?.isTab) {
                const second = current?.children?.find((n) => pathname.includes(n.path));
                targetId = second?.id || '';
                pathText = (second?.isTab ? second?.children?.find((n) => pathname.includes(n.path))?.name : second?.name) ?? '';
            } else {
                pathText = current.name;
            }
            this.openTabIds = _.compact([current.id, targetId]);
            // 设置头部栏
            this.topBarState?.updateTitle(pathText);
        }
    }

    /**
     * 设置滚动条到顶
     */
    scrollIntoTop() {
        const Dom = document.querySelector('html');
        if (Dom) Dom.scrollIntoView(true);
    }


    /**
     * 获取APP数据
     */
    async getAppData() {

    }

    /**
     * 获取rbp餐厅的国家
     */
    async getRBPRestaurantCountry() {
        // 并行请求国家和货币信息
        const [countryRes, currencyNameRes] = await Promise.all([
            apiManage_rbp.get_restaurant_info(),
            apiManage_rbp.get_rbp_settings_currency()
        ]);

        // 设置国家
        let country = '';
        // 通过隧道拿到的货币
        let currencyNameLocal = '';
        if (!_.get(countryRes, 'error')) {
            country = _.get(countryRes, 'name', '');
            currencyNameLocal = _.get(countryRes, 'currency_name', '');
            LSM.set({ path: LOCAL_STORAGE_KEYS.RBP_RESTAURANT_COUNTRY, value: country });
        }
        // 设置货币
        let currencyNameCloud = '';
        if (!_.get(currencyNameRes, 'error')) {
            currencyNameCloud = currencyNameRes;
        }
        // 隧道获取的货币，同步到云端的货币
        const value = currencyNameLocal || currencyNameCloud || '€';
        this.setRbpCurrency(value);
    }
    /**
     * 展平
     * @param routes
     * @returns
     */
    flatRoutes(routes: RoutesType[]) {
        try {
            const newRoutes: RoutesType[] = [];
            routes.forEach((item) => {
                newRoutes.push(item);
                if (item.children) {
                    newRoutes.push(...this.flatRoutes(item.children));
                }
            })
            return newRoutes;
        } catch (error) { return [] }
    }

    /**
     * 多级折叠
     */
    fold(id: string) {
        const openTabIds = this.isMobileDevice && !this.open ? this.openTabletTabIds : this.openTabIds;
        const level = id.split('_').length - 2;
        if (level >= 0) {
            openTabIds[level] = openTabIds[level] === id ? undefined : id;
            if (this.isMobileDevice && !this.open) {
                this.openTabletTabIds = openTabIds;
            } else {
                this.openTabIds = openTabIds;
            }
        }
    }

    /**
     * 点击侧边栏
     */
    clickSidebar(path: RoutesType) {
        if (this.isMobileDevice) this.expendSiderbar(false);
        const userInfoIsNew = (accountState?.userInfo as any)?.is_edit_pass;
        const isNewAccount = userInfoIsNew !== undefined && userInfoIsNew === false;
        if (isNewAccount) {
            GlobalNotification.warning(`${i18n.t('account_manager_please_modify_password_then_work')}`);
            routerManager.history.replace('/main/account');
            return
        }
        routerManager.history.replace(path.path);
        this.topBarState?.updateTitle(path.name);
        this.topBarState?.updateSubTitle('');
    }
    /**
     * 收缩侧边栏
     * @param open
     */
    expendSiderbar(open: boolean) {
        this.open = open;
        this.collapseState = open;
        if (this.isMobileDevice) {
            document.body.style.overflow = this.open ? 'hidden' : 'auto';
        }
        const t = setTimeout(() => {
            if (open) {
                runInAction(() => {
                    this.collapseState = false;
                    this.openTabletTabIds = [];
                })
                this.autoExpend();
            }
            clearTimeout(t)
        }, 200);
    }

    // 监听ws事件
    public addWebsocketListeners() {
        // // 连接
        // DefaultWebsocketClient.removeListener('CONNECT', this.onHandleConnect)
        // DefaultWebsocketClient.addListener('CONNECT', this.onHandleConnect)

    }

    /**
     * 连接断开
     */
    onHandleDisConnect() {
        GlobalNotification.info(i18n.t('ws_connection_interrupted'));
    }
    /**
     * 连接断开
     */
    onHandleConnect() {
        GlobalNotification.info(i18n.t('ws_connect_succeed'));
    }

    /**
     * 货币
     */
    @autoSet(LOCAL_STORAGE_KEYS.RBP_RESTAURANT_CURRENCY)
    setRbpCurrency(value: string) {
        this.rbpCurrency = value;
    }
}



type Props = {
    routes: RoutesType[];
}

/**
 * 获取RBP餐厅stringId
 */
export function getRBPStringId() {
    let string_id = '';
    const mainPageData = MobxObjectsManager.get(MainPageState);
    string_id = mainPageData?.currentRestaurant?.string_id || '';
    if (!string_id) {
        const defaultRestaurant = LSM.get(LOCAL_STORAGE_KEYS.DEFAULT_RESTAURANT);
        string_id = defaultRestaurant?.string_id || '';
    }
    return string_id
}

interface Class<T> {
    // new MyClass(...args) ==> T
    new(...args: any[]): T;
    // Other static properties/operations
    [property: string]: any;
}

/**
 * 切换餐厅监听&校验隧道服务 装饰器
 * - 只适用于 Class 类组件
 */
export function CurrentRestaurantDecorator(target: Class<React.Component>) {
    const didMount = target.prototype.componentDidMount;
    const RenderFn = target.prototype.render;
    const next = target.prototype.componentWillUnmount;

    target.prototype.componentDidMount = function () {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        // eslint-disable-next-line prefer-rest-params
        const thatArguments = arguments;
        /** 是否已执行原 `componentDidMount`（隧道通过后仅一次） */
        let didMountInvoked = false;
        /** MobX 监听全局隧道 Store */
        let tunnelAutorunDisposer: IReactionDisposer | null = null;

        /**
         * 根据 `tunnelStatusStore` 切换渲染：通过则恢复业务 render 并触发原 didMount。
         */
        const applyTunnelGateFromStore = () => {
            const sid = getRBPStringId();
            const { passStatus, currentStringId, tunnelFailLine, dbVersionGateMessage } = tunnelStatusStore;

            if (!sid) {
                target.prototype.render = () => (
                    <TunnelStateComp content={i18n.t('business_analysis_page_none_restaurant')} />
                );
                that.setState({});
                return;
            }

            if (currentStringId !== sid || passStatus === 0) {
                target.prototype.render = () => (
                    <TunnelStateComp content={i18n.t('general_text_checking_tunnel_service')} />
                );
                that.setState({});
                return;
            }

            if (passStatus === 1) {
                target.prototype.render = RenderFn;
                that.setState({});
                if (!didMountInvoked && didMount) {
                    didMountInvoked = true;
                    didMount.call(that, thatArguments);
                }
                return;
            }

            if (passStatus === -3) {
                target.prototype.render = () => <TunnelStateComp content={dbVersionGateMessage} />;
                that.setState({});
                return;
            }

            if (passStatus === -2) {
                target.prototype.render = () => (
                    <TunnelStateComp content={i18n.t('business_analysis_page_none_restaurant')} />
                );
                that.setState({});
                return;
            }

            target.prototype.render = () => (
                <TunnelStateComp
                    content={tunnelFailLine || i18n.t('business_analysis_page_msg_no_database_config')}
                />
            );
            that.setState({});
        };

        tunnelAutorunDisposer = autorun(applyTunnelGateFromStore);
        createPageReqDocker.call(this);
        (that as { __tunnelAutorunDisposer?: IReactionDisposer }).__tunnelAutorunDisposer = tunnelAutorunDisposer;
    };

    target.prototype.componentWillUnmount = function () {
        (this as { __tunnelAutorunDisposer?: IReactionDisposer }).__tunnelAutorunDisposer?.();
        (this as { __tunnelAutorunDisposer?: IReactionDisposer }).__tunnelAutorunDisposer = undefined;
        removeReq.call(this);
        // eslint-disable-next-line prefer-rest-params
        if (next) next.call(this, ...arguments);
    };
}

/**
 * 切换餐厅监听&校验隧道服务 装饰器 Hook（读全局 `tunnelStatusStore`，Tab 切换不重复校验）
 */
export function HookDecorator(HookFn: () => JSX.Element) {
    const HookDecoratorInner = observer(function HookDecoratorObserved() {
        const { passStatus, tunnelFailLine, dbVersionGateMessage } = tunnelStatusStore;

        if (passStatus === 1) {
            return <HookFn />;
        }
        if (passStatus === -3) {
            return <TunnelStateComp content={dbVersionGateMessage} />;
        }
        if (passStatus === -1) {
            return (
                <TunnelStateComp
                    content={tunnelFailLine || i18n.t('business_analysis_page_msg_no_database_config')}
                />
            );
        }
        if (passStatus === -2) {
            return <TunnelStateComp content={i18n.t('business_analysis_page_none_restaurant')} />;
        }
        return <TunnelStateComp content={i18n.t('general_text_checking_tunnel_service')} />;
    });

    return HookDecoratorInner;
}

/**
 * loading 装饰器
 */
export async function LoadingDecorator() {
    return function (target, name, descriptor) {
        const originFn = descriptor.value;
        if (typeof originFn === 'function') {
            descriptor.value = async function (...args) {
                setLoading(true);
                await originFn.apply(this, args);
                setLoading(false);
            }
        }
    }
}

/**
 * 插入loading
 */
export async function LoadingInsert(fn: () => Promise<void>) {
    if (fn && typeof fn === 'function') {
        setLoading(true);
        await fn();
        setLoading(false);
    }
}

function TunnelStateComp({ content }: { content?: string }) {
    return <Card style={{ fontSize: '1.2rem', padding: '20px', alignItems: 'center', display: 'flex' }}>{content}</Card>

}
